import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { MyErrorStateMatcher } from 'src/app/auth/login/login.component';
import { UserService } from 'src/app/shared/user.service';
import { Attributes } from '../../../shared/attributes.model';
import { ContractTypeService } from '../../../shared/contract-type.service';

@Component({
  selector: 'app-attribute-form',
  templateUrl: './attribute-form.component.html',
  styleUrl: './attribute-form.component.css'
})
export class AttributeFormComponent implements OnInit {
  @ViewChild('sectionNameInput') sectionNameInput!: ElementRef<HTMLInputElement>;

  filteredOptions: Observable<string[]>;
  matcher = new MyErrorStateMatcher();
  id: any;
  mode: any;
  formLoading: boolean = false;
  metadataForm: FormGroup;
  isLoading = false;
  dataTypes: any[] = [];
  contracts = [];
  contractFroms = [];
  metaDataList: any[] = [];
  attributeSequenceNumber: number = 0;
  allMetaDataList: any[] = [];
  isSequenceNumberValid: boolean = true;
  sectionNames: string[] = [];
  showDropdown: boolean = false;
  values: string[] = [];
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  announcer = inject(LiveAnnouncer);
  isBooleanError: boolean = false;

  constructor(public userService: UserService, public service: ContractTypeService, private router: Router, private route: ActivatedRoute, private formBuilder: FormBuilder, private toast: ToastrService) {
    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.mode = params['mode'];
    });

    if (this.id)
      this.formLoading = true;

    this.metadataForm = this.formBuilder.group({
      label: ['', [Validators.required]],
      metaDataTypeId: [null, [Validators.required]],
      metaDataValues: [null],
      defaultValue: [''],
      isRequired: [true],
      isEditable: [true],
      isHidden: [false],
      helpText: [''],
      isDisplaySummary: [true],
      isEditablePostExecution: [true],
      isSearchEnabled: [true],
      sequenceNo: [1, [Validators.required, Validators.min(1)]],
      seedAttributeId: [0],
    });

    if (this.mode == 'view') {
      this.metadataForm.disable();
    }
  }

  async ngOnInit() {
    await this.service.getAttributeMetadataType().then(res => {
      this.dataTypes = res as Array<Object>;
    });

    await this.service.getContractList().then(res => {
      this.contracts = res as Array<Object>;
    });

    this.initializeContract();

    // Watching for changes in the sequenceNo field
    this.metadataForm.get('sequenceNo').valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.validateSequenceNumber();
      });

    this.metadataForm.get('metaDataTypeId').valueChanges
      .pipe(
        distinctUntilChanged()
      )
      .subscribe(val => {
        this.metaDataTypeTrigger();
      });
  }

  editMetaData(data) {
    // Handle date fields: if defaultValue is epoch date (1/1/1970), set it to null
    if (data.metaDataTypeId === 9 && data.defaultValue) {
      const dateValue = new Date(data.defaultValue);
      // Check if date is epoch (January 1, 1970) or invalid
      if (isNaN(dateValue.getTime()) || dateValue.getTime() === 0 || 
          (dateValue.getFullYear() === 1970 && dateValue.getMonth() === 0 && dateValue.getDate() === 1)) {
        data.defaultValue = null;
      }
    }
    
    this.metadataForm.patchValue(data);
    this.metaDataList = this.allMetaDataList;
    if (this.metadataForm.get('metaDataValues').value)
      this.values = this.metadataForm.get('metaDataValues').value.split(/\s*,\s*/);
    this.metaDataList = this.metaDataList.filter((option) => {
      return option.seedAttributeId != this.metadataForm.get('seedAttributeId').value
    });


    if (this.mode == 'view')
      this.metadataForm.disable();
    else
      this.metadataForm.enable();
  }


  async initializeContract() {
    await this.service.getAttributeList().then((res: any) => {
      this.metaDataList = res;
      this.allMetaDataList = res;

      this.metaDataList.forEach((attrib: any) => {
        this.attributeSequenceNumber = (attrib.sequenceNo > this.attributeSequenceNumber) ? (attrib.sequenceNo) : (this.attributeSequenceNumber);
      });

      if (this.attributeSequenceNumber) {
        this.metadataForm.get('sequenceNo').setValue(this.attributeSequenceNumber + 1);
      }
    });

    this.formLoading = false;

    if (this.id) {
      const result = this.metaDataList.find(option => option.seedAttributeId == this.id);
      this.editMetaData(result);
    }

  }


  metaDataTypeTrigger() {
    const supportedDataTypeIds = [2, 15, 12];
    if (supportedDataTypeIds.includes(this.metadataForm.get('metaDataTypeId').value)) {
      this.showDropdown = true;
      this.metadataForm.get('metaDataValues').setValidators(Validators.required);
      this.metadataForm.get('metaDataValues').updateValueAndValidity();
    } else {
      this.showDropdown = false;
      this.metadataForm.get('metaDataValues').setValue(null);
      this.values = [];
      this.metadataForm.get('metaDataValues').setValidators(null);
      this.metadataForm.get('metaDataValues').updateValueAndValidity();
    }

    if (this.metadataForm.get('metaDataTypeId').value == 12) {
      this.metadataForm.get('metaDataValues').setValue(null);
      this.values = [];
    }

    // Handle date fields: if switching to date type and defaultValue is empty/invalid, set to null
    if (this.metadataForm.get('metaDataTypeId').value == 9) {
      const defaultValue = this.metadataForm.get('defaultValue').value;
      if (!defaultValue || 
          defaultValue === '' || 
          (defaultValue instanceof Date && (isNaN(defaultValue.getTime()) || defaultValue.getTime() === 0))) {
        this.metadataForm.get('defaultValue').setValue(null);
      }
    }

    if (this.metadataForm.get('metaDataTypeId').value == 10) {
      this.metadataForm.get('isRequired').disable();
      this.metadataForm.get('isRequired').setValue(false);
      this.metadataForm.get('isEditable').disable();
      this.metadataForm.get('isEditable').setValue(false);
    } else {
      this.metadataForm.get('isRequired').enable();
      this.metadataForm.get('isEditable').enable();
    }

  }

  toggleRequiredHidden(value: string) {
    if (value === 'required' && this.metadataForm.get('isRequired').value && this.metadataForm.get('isHidden').value)
      this.metadataForm.get('isHidden').setValue(false);
    if (value === 'required' && this.metadataForm.get('isRequired').value && !this.metadataForm.get('isEditable').value)
      this.metadataForm.get('isEditable').setValue(true);
    if (value === 'hidden' && this.metadataForm.get('isRequired').value && this.metadataForm.get('isHidden').value)
      this.metadataForm.get('isRequired').setValue(false);
    if (value === 'editable' && this.metadataForm.get('isRequired').value && !this.metadataForm.get('isEditable').value)
      this.metadataForm.get('isRequired').setValue(false);
  }

  async saveAttribute(event: Event) {
    event.preventDefault();
    const metaDataValues = this.metadataForm.get('metaDataValues');
    if (metaDataValues.value) metaDataValues.setValue(this.values.join(','));
    this.isLoading = true;
    
    if (this.metadataForm.valid) {
      const data = this.metadataForm.value;
      
      // Handle date fields: if defaultValue is empty or invalid for date type, set it to null
      if (data.metaDataTypeId === 9) {
        const defaultValue = data.defaultValue;
        // Check if defaultValue is empty, null, undefined, or an invalid date (epoch date)
        if (!defaultValue || 
            defaultValue === '' || 
            (defaultValue instanceof Date && (isNaN(defaultValue.getTime()) || defaultValue.getTime() === 0))) {
          data.defaultValue = null;
        }
      }
      
      try {
        if (!this.id) {
          await this.service.saveAttribute(data);
          this.toast.success('Attribute has been Added successfully', '');
        } else {
          await this.service.updateSeedAttribute(data);
          this.toast.success('Data has been updated successfully', '');
        }
      } catch (error) {
        this.toast.error('Something went wrong, please try again later!', '');
      }
      this.isLoading = false;
      this.metadataForm.reset();
      this.initializeContract();
      this.router.navigate(['/app/attributes']);
    }
  }

  reset() {
    this.metadataForm.reset();
    this.metaDataList = this.allMetaDataList;
    if (this.attributeSequenceNumber) {
      this.metadataForm.get('sequenceNo').setValue(this.attributeSequenceNumber + 1);
    }
  }

  validateSequenceNumber() {
    const sequenceNo = this.metadataForm.get('sequenceNo').value;
    const isSequenceNoExist = this.metaDataList.some((attribute: Attributes) => attribute.sequenceNo === sequenceNo);

    this.isSequenceNumberValid = !isSequenceNoExist;

    if (!isSequenceNoExist) {
      const sequenceNoControl = this.metadataForm.get('sequenceNo');
      sequenceNoControl.setErrors(null);
      sequenceNoControl.setValidators([Validators.required, Validators.min(1)]);
      sequenceNoControl.updateValueAndValidity();
    } else {
      this.metadataForm.get('sequenceNo').setErrors({ 'incorrect': true });
    }
  }

  getDropdownLabel(): string {
    switch (this.metadataForm.get('metaDataTypeId').value) {
      case 2:
        return 'Dropdown Values';
      case 15:
        return 'Multi Dropdown Values';
      case 12:
        return 'Boolean Values';
      default:
        return '';
    }
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      if (this.metadataForm.get('metaDataTypeId').value == 12) {
        if (this.values.length < 2) {
          this.isBooleanError = false;
          this.values.push(value);
        } else {
          this.isBooleanError = true;
          this.metadataForm.get('metaDataValues').setErrors({ 'incorrect': true });
        }
      } else {
        this.isBooleanError = false;
        this.values.push(value);
      }
    }

    event.chipInput!.clear();
  }

  remove(value): void {
    const index = this.values.indexOf(value);
    this.isBooleanError = false;
    if (index >= 0) {
      this.values.splice(index, 1);
      this.announcer.announce(`Removed ${value}`);
    }
  }

  edit(val, event: MatChipEditedEvent) {
    const value = event.value.trim();
    this.isBooleanError = false;
    if (!value) {
      this.remove(val);
      return;
    }

    const index = this.values.indexOf(val);
    if (index >= 0) {
      this.values[index] = value;
    }
  }
}
